import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import axios from 'axios';
import { UserRole } from '@smart-contacts/shared';

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive: boolean;
  isOnboardingComplete: boolean;
  dateOfBirth?: string;
  phone?: string;
  lastLogin?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string; requiresOnboarding?: boolean }>;
  loginWithToken: (ssoToken: string) => Promise<void>;
  logout: () => void;
  register: (userData: RegisterData) => Promise<{ success: boolean; message?: string }>;
  completeOnboarding: (data: OnboardingData) => Promise<{ success: boolean; message?: string }>;
  createAdmin: (adminData: AdminData) => Promise<{ success: boolean; message?: string }>;
  updateProfile: (data: ProfileData) => Promise<{ success: boolean; message?: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message?: string }>;
  checkFirstUser: () => Promise<{ success: boolean; isFirstUser?: boolean; message?: string }>;
  loading: boolean;
  isAuthenticated: boolean;
  hasRole: (roles: UserRole[]) => boolean;
  canCreateRole: (targetRole: UserRole) => boolean;
  isSSO: boolean;
}

interface RegisterData {
  email: string;
  role?: UserRole;
}

interface OnboardingData {
  token: string;
  password: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  phone?: string;
}

interface AdminData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  // Deploy-time bootstrap secret. Travels as the x-setup-secret header, never
  // in the request body.
  setupSecret?: string;
}

interface ProfileData {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  phone?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const envApiUrl = (import.meta as any).env.VITE_API_URL as string | undefined;
const AXIOS_BASE_URL = (envApiUrl || '').replace(/\/+$/, '');

axios.defaults.baseURL = AXIOS_BASE_URL;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('token');
  });
  const [loading, setLoading] = useState(true);
  const [isSSO, setIsSSO] = useState<boolean>(false);
  const parentNotifiedRef = useRef(false);
  const initializingRef = useRef(true);

  // Set up axios interceptor for authentication
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        const baseURL = config.baseURL || axios.defaults.baseURL || '';
        if (baseURL && typeof window !== 'undefined') {
          try {
            const u = new URL(baseURL, window.location.origin);
            const basePath = u.pathname.replace(/\/+$/, '');
            if (basePath === '/api' && typeof config.url === 'string') {
              config.url = config.url.replace(/^\/api(\/|$)/, '/');
            }
          } catch {
          }
        }
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => axios.interceptors.request.eject(interceptor);
  }, [token]);

  // Sync token to localStorage
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }, [token]);

  // Set up axios interceptor for handling 401 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Don't auto-logout during SSO initialization — a stale token in
        // localStorage may trigger a 401 before the new SSO token is applied.
        if (error.response?.status === 401 && !initializingRef.current) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const extractSSOToken = () => {
          const searchParams = new URLSearchParams(window.location.search);
          const fromSearch = searchParams.get('ssoauth');
          if (fromSearch) return fromSearch;

          const rawHash = window.location.hash || '';
          if (!rawHash) return null;

          const hash = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
          const queryPart = hash.includes('?') ? hash.split('?')[1] : (hash.includes('=') ? hash : '');
          if (!queryPart) return null;

          const hashParams = new URLSearchParams(queryPart);
          return hashParams.get('ssoauth');
        };

        const ssoToken = extractSSOToken();
        if (ssoToken) {
          // Clear any stale token before SSO login to prevent the request
          // interceptor from attaching an expired token to concurrent requests.
          localStorage.removeItem('token');
          setToken(null);
          await loginWithToken(ssoToken);
          return;
        }

        const storedSSO = sessionStorage.getItem('is_sso_login') === 'true';
        if (storedSSO) {
          setIsSSO(true);
        }

        const storedToken = localStorage.getItem('token');
        if (!storedToken) {
          return;
        }

        const response = await axios.get('/api/auth/profile');
        if (response.data.success) {
          setUser(response.data.data.user);
        }
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem('token');
          setToken(null);
          setIsSSO(false);
          sessionStorage.removeItem('is_sso_login');
        }
      } finally {
        initializingRef.current = false;
        setLoading(false);
        if (typeof window !== 'undefined' && window.parent && window.parent !== window && !parentNotifiedRef.current) {
          parentNotifiedRef.current = true;
          try {
            window.parent.postMessage({ type: 'child-ready' }, '*');
          } catch {
          }
        }
      }
    };

    initAuth();
  }, []);

  // Set up message listener for chrome extension token requests
  useEffect(() => {
    const handleExtensionMessage = (event: MessageEvent) => {
      // Verify origin for security
      // Allow: chrome extensions, same origin, null (local), and trusted ecosystem domains
      const trustedDomains = [
        'https://apps.noloop.eu',
        'http://localhost:3000',
        'http://localhost:5000'
      ];

      const isValidOrigin =
        event.origin.startsWith('chrome-extension://') ||
        event.origin === window.location.origin ||
        event.origin === 'null' || // For local testing
        trustedDomains.includes(event.origin);

      if (!isValidOrigin) {
        console.warn('[AuthContext] Rejected message from untrusted origin:', event.origin);
        return;
      }

      if (event.data && event.data.type === 'REQUEST_AUTH_TOKEN') {
        console.log('[AuthContext] Received token request from extension');

        if (token && user) {
          // User is authenticated, send token back
          const response = {
            type: 'AUTH_TOKEN_RESPONSE',
            success: true,
            token: token,
            user: {
              _id: user._id,
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              role: user.role
            }
          };

          console.log('[AuthContext] Sending token to extension');
          event.source?.postMessage(response, { targetOrigin: event.origin } as any);
        } else {
          // User not authenticated
          const response = {
            type: 'AUTH_TOKEN_RESPONSE',
            success: false,
            error: 'User not authenticated'
          };

          console.log('[AuthContext] User not authenticated, sending error');
          event.source?.postMessage(response, { targetOrigin: event.origin } as any);
        }
      }
    };

    window.addEventListener('message', handleExtensionMessage);
    return () => window.removeEventListener('message', handleExtensionMessage);
  }, [token, user]);

  const loginWithToken = async (ssoToken: string) => {
    console.log('[AuthContext] loginWithToken called with token:', ssoToken ? 'present' : 'missing');
    try {
      sessionStorage.setItem('is_sso_login', 'true');
      setIsSSO(true);
      localStorage.setItem('token', ssoToken);
      setToken(ssoToken);
      
      console.log('[AuthContext] Fetching profile with new SSO token...');
      const response = await axios.get('/api/auth/profile', {
        headers: { Authorization: `Bearer ${ssoToken}` }
      });
      
      if (response.data.success) {
        console.log('[AuthContext] SSO Profile loaded successfully:', response.data.data.user);
        setUser(response.data.data.user);
        console.log('[AuthContext] Removing ssoauth param from URL');
        const url = new URL(window.location.href);
        url.searchParams.delete('ssoauth');
        if (url.hash && url.hash.includes('ssoauth=')) {
          const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
          const [hashPath, hashQuery] = hash.split('?');
          if (hashQuery) {
            const hashParams = new URLSearchParams(hashQuery);
            hashParams.delete('ssoauth');
            const nextHashQuery = hashParams.toString();
            url.hash = nextHashQuery ? `${hashPath}?${nextHashQuery}` : hashPath;
          } else {
            const hashParams = new URLSearchParams(hash);
            hashParams.delete('ssoauth');
            const nextHash = hashParams.toString();
            url.hash = nextHash ? nextHash : '';
          }
        }
        window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
      }
    } catch (error: any) {
      console.error('[AuthContext] SSO Login failed:', error);
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        setToken(null);
        setIsSSO(false);
        sessionStorage.removeItem('is_sso_login');
      }
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      
      if (response.data.success) {
        const { token: newToken, user: userData } = response.data.data;
        setToken(newToken);
        setUser(userData);
        localStorage.setItem('token', newToken);
        return { success: true };
      } else {
        return { 
          success: false, 
          message: response.data.message,
          requiresOnboarding: response.data.requiresOnboarding 
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
        requiresOnboarding: error.response?.data?.requiresOnboarding
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
     setIsSSO(false);
     sessionStorage.removeItem('is_sso_login');
  };

  const register = async (userData: RegisterData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      return {
        success: response.data.success,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  const completeOnboarding = async (data: OnboardingData) => {
    try {
      const response = await axios.post('/api/auth/onboard', data);
      
      if (response.data.success) {
        const { token: newToken, user: userData } = response.data.data;
        setToken(newToken);
        setUser(userData);
        localStorage.setItem('token', newToken);
      }
      
      return {
        success: response.data.success,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Onboarding failed'
      };
    }
  };

  const createAdmin = async (adminData: AdminData) => {
    try {
      const { setupSecret, ...body } = adminData;
      // Deploy-time bootstrap secret (SETUP_SECRET on the backend). Sent as a
      // header so it never ends up in a URL, an access log, or the user document.
      const headers = setupSecret ? { 'x-setup-secret': setupSecret } : undefined;
      const response = await axios.post('/api/auth/create-admin', body, { headers });

      if (response.data.success) {
        const { token: newToken, user: userData } = response.data.data;
        setToken(newToken);
        setUser(userData);
        localStorage.setItem('token', newToken);
      }
      
      return {
        success: response.data.success,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Admin creation failed'
      };
    }
  };

  const updateProfile = async (data: ProfileData) => {
    try {
      const response = await axios.put('/api/auth/profile', data);
      
      if (response.data.success) {
        setUser(response.data.data.user);
      }
      
      return {
        success: response.data.success,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Profile update failed'
      };
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const response = await axios.put('/api/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      return {
        success: response.data.success,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Password change failed'
      };
    }
  };

  const hasRole = (roles: UserRole[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  const canCreateRole = (targetRole: UserRole): boolean => {
    if (!user) return false;
    
    switch (user.role) {
      case 'admin':
        return true; // Admins can create any role
      case 'super_user':
        return targetRole === 'super_user' || targetRole === 'user'; // Super users can create super users and users
      case 'user':
        return false; // Users cannot create other users
      default:
        return false;
    }
  };

  const checkFirstUser = async () => {
    try {
      const response = await axios.get('/api/auth/startup');
      return {
        success: response.data.success,
        isFirstUser: response.data.data?.isFirstUser || false,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to check startup status'
      };
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    register,
    completeOnboarding,
    createAdmin,
    updateProfile,
    changePassword,
    checkFirstUser,
    loginWithToken,
    loading,
    isAuthenticated: !!user,
    hasRole,
    canCreateRole,
    isSSO
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export type { User, RegisterData, OnboardingData, AdminData, ProfileData };
